<template>
  <div :class="['question-builder-container', 'level-' + level]">
    <div class="row single-quesion-box" :style="{ marginLeft: (level * 5) + 'px' }">
      <h3 style="text-align:center;color:#782B55;margin-bottom:15px;font-weight:bold;">
        <span v-if="level > 0" style="color:#999;">↳ Follow-up </span>
        Question {{ displayQuestionNumber }}
      </h3>

      <!-- Question Text Input -->
      <div class="col-xs-12 col-sm-6">
        <input
          type="text"
          maxlength="60"
          :placeholder="'Enter Question ' + displayQuestionNumber + ' Text'"
          :name="fieldName('surveyQuestion')"
          v-model="question.question"
          v-validate="'required'"
          :class="{'input': true, 'is-danger': errors.has('add-survey.' + fieldName('surveyQuestion')) }"
        >
      </div>

      <!-- Question Type Select -->
      <div class="col-xs-12 col-sm-6">
        <select
          :name="fieldName('surveyQuestionType')"
          v-model="question.questionType"
          v-validate="'required'"
          :class="{'input': true, 'is-danger': errors.has('add-survey.' + fieldName('surveyQuestionType')) }"
        >
          <option value="">Select Answer Type</option>
          <option value="SINGLE_ANSWER" v-if="basicSurveyDetails.type !=='NON_GEO'">Single Select</option>
          <option value="MULTIPLE_ANSWER" v-if="basicSurveyDetails.type !=='NON_GEO'">Multi Select</option>
          <option value="TEXT">Textual Value</option>
        </select>
      </div>

      <!-- Answers Section -->
      <div v-if="question.questionType !== 'TEXT' && basicSurveyDetails.type !=='NON_GEO'" class="col-xs-12">
        <div class="answer-section" v-for="(answer, ai) in question.answers" :key="ai">
          <div class="row answer-container">
            <div class="col-xs-12 col-sm-6">
              <input
                maxlength="40"
                type="text"
                :placeholder="'Answer '+ (ai+1)"
                :name="fieldName('surveyAnswers', ai)"
                v-model="answer.answerOption"
                class="answer-input"
                v-validate="'required'"
                :class="{'input': true, 'is-danger': errors.has('add-survey.' + fieldName('surveyAnswers', ai)) }"
              >
              <a href="javascript:;" v-if="ai > 1" @click="removeAnswerHandler(ai)" class="pull-right remove-answer-button">x</a>
            </div>

            <!-- Add Follow-up Button (hidden after one follow-up) -->
            <div class="col-xs-12 col-sm-6" v-if="basicSurveyDetails.surveyType == 'conditional_base'">
              <button
                type="button"
                class="add-followup-btn"
                @click="addFollowUpHandler(ai)"
                v-if="canAddFollowUp(ai)"
              >
                + Add Follow-up Question
              </button>
            </div>
          </div>

          <!-- Recursive Follow-up Questions -->
          <div v-if="answer.followUpQuestions && answer.followUpQuestions.length > 0" class="followup-questions-container">
            <QuestionBuilder
              v-for="(followUpQuestion, fqi) in answer.followUpQuestions"
              :key="'followup-' + ai + '-' + fqi"
              :question="followUpQuestion"
              :questionIndex="fqi"
              :questionPath="[...questionPath, ai, fqi]"
              :parentPath="questionPath"
              :level="level + 1"
              :basicSurveyDetails="basicSurveyDetails"
              :errors="errors"
              :validator="validator"
              @remove-question="removeFollowUpHandler(ai, fqi)"
              @add-answer="bubbleEvent('add-answer', $event)"
              @remove-answer="bubbleEvent('remove-answer', $event)"
              @add-followup="bubbleEvent('add-followup', $event)"
              @remove-followup="bubbleEvent('remove-followup', $event)"
            />
          </div>
        </div>

        <!-- Add More Answer Button -->
        <a
          class="add-more-ans"
          v-if="canAddMoreAnswers"
          href="javascript:;"
          @click="addAnswerHandler"
        >
          Add Another Answer
        </a>
      </div>

      <!-- Delete Question Buttons -->
      <a
        href="javascript:;"
        @click="removeQuestionHandler"
        class="pull-right delete-question-link"
        v-if="level === 0 && questionIndex > 0"
      >
        Delete Question
      </a>

      <a
        href="javascript:;"
        @click="removeQuestionHandler"
        class="pull-right delete-question-link"
        v-if="level > 0"
      >
        Delete Follow-up
      </a>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuestionBuilder',
  props: {
    question: { type: Object, required: true },
    questionIndex: { type: Number, required: true },
    questionPath: { type: Array, required: true },
    parentPath: { type: Array, required: true },
    level: { type: Number, default: 0 },
    basicSurveyDetails: { type: Object, required: true },
    errors: { type: Object, required: true },
    validator: { type: Object, required: true }
  },
  computed: {
    displayQuestionNumber () {
      return this.questionIndex + 1
    },
    canAddMoreAnswers () {
      if (this.question.questionType === 'TEXT') return false
      if (this.basicSurveyDetails.type === 'PICKL') {
        return this.question.answers.length < this.$store.state.settings.max_answers_per_survey_question
      }
      if (this.basicSurveyDetails.type === 'MEDIA') {
        return this.question.answers.length < this.$store.state.settings.max_answers_per_media_survey_question
      }
      return false
    }
  },
  methods: {
    fieldName (type, answerIndex = null) {
      const pathString = this.questionPath.join('_')
      return answerIndex !== null ? `${type}_${pathString}_${answerIndex}` : `${type}_${pathString}`
    },

    canAddFollowUp (answerIndex) {
      const answer = this.question.answers[answerIndex]
      const hasFollowUp = answer.followUpQuestions && answer.followUpQuestions.length > 0
      return (this.level === 0 && this.question.questionType === 'SINGLE_ANSWER' && !hasFollowUp)
    },

    addAnswerHandler () {
      this.$emit('add-answer', { path: this.questionPath })
    },

    removeAnswerHandler (answerIndex) {
      this.$emit('remove-answer', { questionPath: this.questionPath, answerIndex })
    },

    addFollowUpHandler (answerIndex) {
      this.$emit('add-followup', { questionPath: this.questionPath, answerIndex })
    },

    removeFollowUpHandler (answerIndex, followUpIndex) {
      this.$emit('remove-followup', { questionPath: this.questionPath, answerIndex, followUpIndex })
    },

    removeQuestionHandler () {
      if (this.level === 0) {
        this.$emit('remove-question', this.questionIndex)
      } else {
        const parentAnswerIndex = this.questionPath[this.questionPath.length - 2]
        this.$emit('remove-question', {
          questionPath: this.parentPath,
          answerIndex: parentAnswerIndex,
          followUpIndex: this.questionIndex
        })
      }
    },

    bubbleEvent (eventName, data) {
      this.$emit(eventName, data)
    }
  }
}
</script>

<style scoped>
.question-builder-container {
  margin-bottom: 8px;
}
.single-quesion-box {
  border: solid 1px rgb(130, 222, 109);
  padding: 1%;
  border-radius: 20px;
  margin: 10px;
  position: relative;
}
.level-1 .single-quesion-box {
  border-color: #9d7db8;
  background-color: #f9f7fb;
}
.level-2 .single-quesion-box {
  border-color: #b8a7c9;
  background-color: #fdfcfe;
}
.answer-section {
  margin-bottom: 15px;
}
.answer-container {
  margin-left: 2%;
  margin: 1%;
}
.answer-input {
  height: 30px;
  border-radius: 10px;
  margin: 0;
  width: 90%;
}
.add-more-ans {
  font-size: 15px;
  text-decoration: none;
  display: block;
  margin-left: 3%;
  color: #782b54;
}
.remove-answer-button {
  background: #f00;
  line-height: 27.58px;
  border: none;
  border-radius: 50%;
  color: white;
  padding: 0px 10px;
  text-decoration: none;
}
.delete-question-link {
  font-size: 16px;
  margin-top: 40px;
  color: #782b54;
}
.add-followup-btn {
  background: #782b54;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.3s;
}
.add-followup-btn:hover {
  background: #574080;
}
.followup-questions-container {
  margin-top: 15px;
  padding-left: 5px;
}
select,
select:hover {
  border: 1px solid #6f3053;
  border-radius: 3px;
  margin-bottom: 20px;
  font-size: 18px;
  line-height: 20px;
  color: black;
  width: 100%;
  padding: 14px 42px 14px 16px;
  height: 50px;
  background-position: right 11px center;
  background-size: 20px 12px;
  background-image: url("/img/Create Pickl/selectproductDropdown_icon.png");
  background-color: white;
}
</style>
